import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FleetPropertyRow {
  id: string;
  name: string;
  rate_cents: number;
  rate_source_resolved: string;
  pv_kwh: number;
  load_kwh: number;
  grid_import_kwh: number;
  grid_export_kwh: number;
  uptime_pct: number;
  self_consumption_pct: number;
  curtailed_kwh: number;
  curtailment_days: number;
  curtailment_value_dollars: number;
  savings_dollars: number;
  battery_grade: string | null;
  degradation_pct: number | null;
  flag_battery_expansion: boolean;
  flag_chronic_curtailment: boolean;
  flag_battery_replacement: boolean;
}

export interface FleetAnalytics {
  fleet_pv_kwh: number;
  fleet_load_kwh: number;
  fleet_grid_import_kwh: number;
  fleet_grid_export_kwh: number;
  fleet_battery_charged_kwh: number;
  fleet_battery_discharged_kwh: number;
  fleet_uptime_pct: number;
  fleet_curtailed_kwh: number;
  fleet_curtailed_hours: number;
  fleet_total_curtailment_dollars: number;
  fleet_self_consumption_pct: number;
  fleet_savings_dollars: number;
  fleet_avg_rate_cents: number;
  critical_count: number;
  warning_count: number;
  info_count: number;
  sent_count: number;
  resolved_count: number;
  properties: FleetPropertyRow[] | null;
  daily_trend: FleetDailyTrendPoint[] | null;
  property_count: number;
  period_days: number;
}

export interface FleetDailyTrendPoint {
  local_date: string;
  pv_kwh: number;
  load_kwh: number;
  grid_import_kwh: number;
  grid_export_kwh: number;
}

export function useFleetAnalytics(orgId: string | undefined, dateFrom: string, dateTo: string) {
  return useQuery({
    queryKey: ['analytics-fleet', orgId, dateFrom, dateTo],
    queryFn: async (): Promise<FleetAnalytics | null> => {
      if (!orgId) return null;

      const { data, error } = await supabase.rpc('get_analytics_fleet_summary', {
        p_org_id: orgId,
        p_date_from: dateFrom,
        p_date_to: dateTo,
      });

      if (error) throw error;
      return data as unknown as FleetAnalytics;
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}
